import { Box } from '@/components/box';
import { JobsContext } from '@/components/careers/context';
import type { JobProps } from '@/components/careers/types';
import { GridContainer } from '@/components/grid-container';
import { Layout } from '@/components/layout';
import type { ToolkitModalProps } from '@/components/toolkit-modal';
import { useHasMounted } from '@/hooks/use-has-mounted';
import { components } from '@/lib/sanity/portable-text';

import { urlForImage } from '@/lib/sanity/sanity';
import {
  PortableText,
  PortableTextComponentsProvider,
} from '@portabletext/react';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  Cookies,
  getCookieConsentValue,
  resetCookieConsentValue,
} from 'react-cookie-consent';

import { ButtonLink } from '@/components/button-link';

import { Text } from '@/components/text';
import { Badge, getBadge } from '@/lib/sanity/queries/get-badge';
import {
  ToolkitItem,
  getToolkitItem,
} from '@/lib/sanity/queries/get-toolkit-item';
import { getLandingPages } from '@/lib/sanity/queries/get-landing-pages';
import { getToolkitItems } from '@/lib/sanity/queries/get-toolkit-items';
import {
  type LandingPage,
  getLandingPage,
} from '@/lib/sanity/queries/get-landing-page';
import { styled } from '@/stitches.config';
import { getExperienceColors } from '@/lib/sanity/queries/get-experience-colors';
import { ExperienceColor } from '@/lib/schemas/experience-colors';
import { useExperienceColorsStore } from '@/hooks/useExperienceColorsStore';

const ToolkitModal = dynamic<ToolkitModalProps>(
  () =>
    import('@/components/toolkit-modal/toolkit-modal').then(
      (mod) => mod.ToolkitModal
    ),
  { ssr: true }
);

function LandingPage(props: InferGetStaticPropsType<typeof getStaticProps>) {
  const {
    content,
    toolkitItem,
    jobs,
    badge,
    contactPersonType,
    experienceColors,
  } = props;

  const setExperienceColors = useExperienceColorsStore(
    (x) => x.setExperienceColors
  );

  setExperienceColors(experienceColors);

  const canonicalTookiItemUrl = toolkitItem
    ? `https://www.cleverfranke.com/services/toolkit/${toolkitItem}`
    : undefined;

  /**
   * Wait, what's this?
   * Well, because we're playing with the modal window in a drill-down page,
   * we're using catch-all routes for project pages. They work really well,
   * but the DX is a bit iffy imho. I'd rather see named paths, but with this
   * array approach we're preventing a re-render of the page when the
   * modal window opens or closes, so... required evil API here.
   */

  const router = useRouter();
  const hasMounted = useHasMounted();

  const { query } = router;

  /**
   * By request the image through the next.js image proxy, we save on traffic to the Sanity CDN
   */
  const ogImage = content.metadata?.openGraphImage
    ? `https://www.cleverfranke.com/_next/image?url=${urlForImage(
        content.metadata?.openGraphImage
      ).url()}&w=1200&q=75`
    : undefined;

  const cookieConsentValue = getCookieConsentValue();

  function handleCookieRejection() {
    resetCookieConsentValue();
    if (window) {
      window.location.reload();
    }
  }

  function handleCookieAccept() {
    Cookies.set('CookieConsent', 'true');
    if (window) {
      window.location.reload();
    }
  }

  return (
    <PortableTextComponentsProvider components={components}>
      <JobsContext.Provider value={jobs}>
        <Layout
          title={content.metadata?.title || undefined}
          description={content.metadata?.description || undefined}
          ogImage={ogImage}
          canonicalToolkitItemUrl={canonicalTookiItemUrl}
          badgeData={badge}
          contactPersonType={contactPersonType}
        >
          <Box
            css={{
              paddingTop: '$13',
              paddingBottom: '$8',
            }}
          >
            <div>
              {content.content && <PortableText value={content.content} />}

              {/* Landing page used for marketing */}
              {router.asPath === '/esg' && hasMounted && (
                <GridContainer
                  id="contact-form"
                  css={{
                    marginTop: '-$7',
                    marginBottom: '$8',
                  }}
                >
                  <StyledContactFormIframe src="https://airtable.com/embed/appQoCE1UHiW6iDFL/pagbnrW2T6Fcp4yBL/form" />
                </GridContainer>
              )}
            </div>

            {router.asPath === '/privacy-policy' && hasMounted && (
              <GridContainer>
                <Box>
                  {cookieConsentValue ? (
                    <>
                      <Text css={{ mb: '1em' }} as="p" size={3}>
                        You have given consent to using cookies on this website.
                        You can revoke your consent.
                      </Text>
                      <div>
                        <ButtonLink onClick={() => handleCookieRejection()}>
                          Remove consent
                        </ButtonLink>
                      </div>
                    </>
                  ) : (
                    <>
                      <Text css={{ mb: '1em' }} as="p" size={3}>
                        You have not accepted cookies on this site. Do you want
                        to accept cookies?
                      </Text>
                      <div>
                        <ButtonLink onClick={() => handleCookieAccept()}>
                          Accept cookies
                        </ButtonLink>
                      </div>
                    </>
                  )}
                </Box>
              </GridContainer>
            )}
          </Box>
        </Layout>
      </JobsContext.Provider>

      <ToolkitModal
        toolkitItem={toolkitItem}
        handleClose={() => {
          router.replace(`/${query?.slug?.[0]}`, undefined, {
            shallow: true,
            scroll: false,
          });
        }}
        isOpen={!!query?.slug?.[2]}
      />
    </PortableTextComponentsProvider>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const pages = (await getLandingPages()).filter(
    (p) => p.slug.current !== 'research'
  );

  // Get the paths we want to pre-render based on all landing page information in sanity
  const paths: Array<{
    params: {
      slug: string[];
    };
  }> = [];

  const toolkitItemsSlugs = await getToolkitItems();

  // Get the paths we want to pre-render based on work
  pages.forEach((page) => {
    paths.push({
      params: {
        slug: [page.slug.current],
      },
    });

    // Because the services page has modal windows for the toolkit items, we need to pre-render the paths
    // and load the data here
    if (page.slug.current === 'services') {
      toolkitItemsSlugs.map((slug: string) => {
        paths.push({
          params: {
            slug: [page.slug.current, 'toolkit', slug],
          },
        });
      });
    }
  });

  // We'll pre-render only these paths at build time.
  // { fallback: blocking } means new routes are loaded on the server
  // before returning a static version to the client.
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<
  {
    content: LandingPage;
    preview: boolean;
    toolkitItem: ToolkitItem | null;
    jobs?: JobProps | null;
    badge: Badge;
    contactPersonType: 'business' | 'careers' | 'esg';
    experienceColors: ExperienceColor[] | null;
  },
  { slug: string[] }
> = async (context) => {
  const { preview = false, params } = context;
  const slug = params?.slug?.[0];

  if (!slug) {
    return { notFound: true, revalidate: 60 * 60 };
  }

  const content = await getLandingPage(preview, slug);

  if (!content) {
    return { notFound: true, revalidate: 60 * 60 };
  }

  // If we defined /toolkit/ but we didn't define /toolkit/item-name, we also want to show a 404.
  // TBD: Do we redirect to the normal services page?
  if (params?.slug?.[1] && !params?.slug?.[2]) {
    return { notFound: true, revalidate: 60 * 60 };
  }

  // Load this data only for the careers page
  let jobs = [];
  if (params?.slug[0] === 'careers') {
    const res = await fetch('https://api.homerun.co/v2/vacancies', {
      headers: {
        Authorization: `Bearer ${process.env.HOMERUN_APIKEY}`,
      },
    });

    jobs = await res.json();

    //only sent back relevant details to Next.js for privacy reasons
    if (jobs.data) {
      jobs = jobs.data
        .filter((job: JobProps) => job.status === 'public')
        .map((job: JobProps) => {
          const {
            job_url,
            created_at,
            title,
            type,
            application_form_url,
            location_type,
            id,
          } = job;
          return {
            job_url,
            created_at,
            title,
            type,
            application_form_url,
            location_type,
            id,
          };
        });
    }
  }

  // Load data only for toolkit pages
  let toolkitItemId;
  let toolkitItem: ToolkitItem | null = null;
  if (params?.slug?.length > 2) {
    toolkitItemId = params?.slug[2];
    toolkitItem = await getToolkitItem(preview, toolkitItemId);
  }

  // Load experienceColors only for services page
  const experienceColors =
    params?.slug[0] === 'services' ? await getExperienceColors(preview) : null;

  const badge = await getBadge(preview);

  const contactPersonType = (() => {
    switch (params?.slug[0]) {
      case 'careers':
        return 'careers';
      case 'esg':
        return 'esg';
      default:
        return 'business';
    }
  })();

  return {
    props: {
      content,
      preview,
      toolkitItem,
      jobs,
      badge,
      contactPersonType,
      experienceColors,
    },
    //           s * min
    revalidate: 60 * 30,
  };
};

export default LandingPage;

const StyledContactFormIframe = styled('iframe', {
  border: 'none',
  width: 'clamp(300px, 100%, 800px)',
  height: 600,
  background: 'transparent',
});
